import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "@/App.css";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { ProblemStatement } from "@/components/landing/ProblemStatement";
import { Features } from "@/components/landing/Features";
import { HowItWorks, Platforms } from "@/components/landing/HowItWorks";
import {
  DashboardSpotlight,
  AISpotlight,
  SEOSpotlight,
  ShippingSpotlight,
} from "@/components/landing/Spotlights";
import { WhyChoose, Testimonials } from "@/components/landing/Social";
import { Pricing, FAQ, FinalCTA } from "@/components/landing/Pricing";
import { Footer } from "@/components/landing/Contact";
import { PrivacyPolicyPage } from "@/components/landing/PrivacyPolicyPage";
import { LabelDeskLandingPage } from "@/components/landing/pages/LabelDeskLandingPage";
import { PriceManagerLandingPage } from "@/components/landing/pages/PriceManagerLandingPage";
import { PriceReducerLandingPage } from "@/components/landing/pages/PriceReducerLandingPage";
import { DashboardLandingPage } from "@/components/landing/pages/DashboardLandingPage";
import { PageMeta } from "@/components/seo/PageMeta";
import { Toaster } from "@/components/ui/sonner";
import { AuthDialogProvider } from "@/lib/auth-dialog-context";
import { AuthProvider } from "@/lib/auth-context";
import { SubscriptionProvider } from "@/lib/subscription-context";
import { SubscriptionPage } from "@/components/panel/pages/SubscriptionPage";
import { PanelLayout } from "@/components/panel/PanelLayout";
import { RequireAuth } from "@/components/panel/RequireAuth";
import { DashboardPage } from "@/components/panel/pages/DashboardPage";
import { UsersPage } from "@/components/panel/pages/UsersPage";
import { PriceManagerPage } from "@/components/panel/pages/PriceManagerPage";
import { PriceReducerPage } from "@/components/panel/pages/PriceReducerPage";
import { ProfilePage } from "@/components/panel/pages/ProfilePage";
import { ProductsPage } from "@/components/panel/pages/ProductsPage";
import { ListingGeneratorPage } from "@/components/panel/pages/ListingGeneratorPage";
import { LabelDeskPage } from "@/components/panel/pages/LabelDeskPage";

const HOMEPAGE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "EasyLister",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Chrome",
  description:
    "AI-powered listing, pricing, and shipping toolkit for Meesho, Flipkart, and Amazon sellers in India.",
  offers: {
    "@type": "Offer",
    priceCurrency: "INR",
    price: "0",
    description: "3-day free trial, paid plans available",
  },
};

const Landing = () => (
  <div className="App font-body text-slate-900 antialiased" data-testid="app-root">
    <PageMeta
      title="EasyLister — AI Listing Generator & Seller Tools for Meesho, Flipkart, Amazon"
      description="Clone products from Amazon, Flipkart & Meesho in one click, generate AI-written titles and descriptions, manage prices in bulk, split shipping labels, and compress images — all in one Chrome extension + dashboard built for Indian sellers."
      keywords="meesho seller tool, flipkart listing tool, amazon product clone, AI product listing india, meesho auto fill, meesho price manager, meesho label splitter, image compressor for meesho, meesho seller dashboard, bulk price update meesho"
      path="/"
      ogTitle="EasyLister — AI Listing Generator & Seller Tools for Meesho, Flipkart, Amazon"
      ogDescription="One Chrome extension for Indian marketplace sellers: AI listings, one-click product cloning, price management, shipping label splitting, and image compression."
      twitterTitle="EasyLister — Sell Smarter on Meesho, Flipkart & Amazon"
      twitterDescription="AI listings, price management, label splitting & image compression — one toolkit for Indian marketplace sellers."
      jsonLd={HOMEPAGE_JSON_LD}
    />
    <Navbar />
    <main>
      <Hero />
      <ProblemStatement />
      <Features />
      <HowItWorks />
      <Platforms />
      <DashboardSpotlight />
      <AISpotlight />
      <SEOSpotlight />
      <ShippingSpotlight />
      <WhyChoose />
      <Testimonials />
      <Pricing />
      <FAQ />
      <FinalCTA />
    </main>
    <Footer />
  </div>
);

function App() {
  return (
    <HelmetProvider>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <AuthDialogProvider>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                {/* Public feature landing pages */}
                <Route path="/labeldesk" element={<LabelDeskLandingPage />} />
                <Route path="/price-manager" element={<PriceManagerLandingPage />} />
                <Route path="/price-reducer" element={<PriceReducerLandingPage />} />
                <Route path="/dashboard" element={<DashboardLandingPage />} />
                <Route
                  path="/app"
                  element={
                    <RequireAuth>
                      <PanelLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="listing-generator" element={<ListingGeneratorPage />} />
                  <Route path="my-listings" element={<Navigate to="/app/listing-generator" replace />} />
                  <Route path="subscription" element={<SubscriptionPage />} />
                  <Route path="wallet" element={<Navigate to="/app/subscription" replace />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="price-manager" element={<PriceManagerPage />} />
                  <Route path="price-reducer" element={<PriceReducerPage />} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route path="label-desk" element={<LabelDeskPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster richColors position="top-center" />
            </AuthDialogProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
